import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Grid, List as ListIcon, Bookmark, Star, ChevronLeft, ChevronRight, Phone, Mail, Award, Check } from "lucide-react";
import {
  getPublicListings,
  getPublicRequirements,
  getMarketplaceProfile,
  MarketplaceListingListItem,
  MarketplaceRequirement,
  applyToRequirement,
  saveListing,
  unsaveListing,
  saveRequirement,
  unsaveRequirement,
  getMarketplaceBanners,
  MarketplaceBanner,
  getMarketplaceFilterOptions,
  MarketplaceFilterOptionsData,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import apiCall from "../../../Redux/ApiCalls/AuthorizedApi";
import { resolveImage } from "../../../Utils/helper";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import styled from "styled-components";
import { checkProfileCompleteness, isAccountFrozen } from "./MarketplaceShared";
import MarketplaceLoader from "./MarketplaceLoader";

import equipmentBannerBg from "../../../assets/images/marketplace/equipment.webp";

import {
  PageWrapper,
  Sidebar,
  SidebarTitle,
  FilterGroup,
  FilterGroupTitle,
  FilterList,
  FilterLabel,
  MainContent,
  TopBar,
  TopBarLeft,
  PageTitle,
  ResultBadge,
  TopBarRight,
  SearchInputWrap,
  SearchInput,
  ToggleGroup,
  ToggleBtn,
  SortSelectWrap,
  EquipmentGrid,
  EquipCard,
  EquipCardImageWrap,
  EquipCardImage,
  TopBadges,
  ConditionBadge,
  VendorBadge,
  FavBtn,
  EquipCardBody,
  TitleRow,
  EquipTitle,
  RatingScore,
  EquipDesc,
  PriceRow,
  PriceBlock,
  PriceLabel,
  PriceValue,
  ActionRow,
  ActionBtnPrimary,
  ActionBtnSecondary,
  BannerBox,
  BannerOverlay,
  BannerContent,
  BannerEyebrow,
  BannerTitle,
  BannerDesc,
  BannerBtn,
  BannerFooter
} from "./MarketplaceEquipmentPageStyles";

const SKELETON_COUNT = 6;

const EquipSkeleton: React.FC<{ viewMode: "grid" | "list" }> = ({ viewMode }) => (
  <EquipCard $viewMode={viewMode} style={{ pointerEvents: "none" }}>
    <EquipCardImageWrap $viewMode={viewMode}>
      <div style={{ width: "100%", height: "100%", background: "var(--mp-cardBackground)", opacity: 0.5 }} />
    </EquipCardImageWrap>
    <EquipCardBody>
      <div style={{ height: 24, width: "80%", borderRadius: 6, background: "var(--mp-cardBackground)", opacity: 0.5, marginBottom: 8 }} />
      <div style={{ height: 16, width: "100%", borderRadius: 4, background: "var(--mp-cardBackground)", opacity: 0.3, marginBottom: 24 }} />
      <div style={{ height: 40, width: "100%", borderRadius: 8, background: "var(--mp-cardBackground)", opacity: 0.4, marginTop: "auto" }} />
    </EquipCardBody>
  </EquipCard>
);

const MarketplaceEquipmentPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<"list" | "requirement">(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam === "requirements" || tabParam === "requirement") return "requirement";
    return "list";
  });
  const [listings, setListings] = useState<MarketplaceListingListItem[]>([]);
  const [requirements, setRequirements] = useState<MarketplaceRequirement[]>([]);
  const [myProfileId, setMyProfileId] = useState<number | null>(null);
  const [myUserId, setMyUserId] = useState<number | null>(null);
  const [revealedContacts, setRevealedContacts] = useState<Record<string, { email: string; phone: string }>>({});
  const [appliedJobs, setAppliedJobs] = useState<Record<string, boolean>>({});
  const [savedListings, setSavedListings] = useState<Record<string, boolean>>({});
  const [savedRequirements, setSavedRequirements] = useState<Record<string, boolean>>({});

  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState<MarketplaceRequirement | null>(null);
  const [applyMessage, setApplyMessage] = useState("");
  const [applyRate, setApplyRate] = useState("");
  const [submittingApply, setSubmittingApply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("search") || "";
  });

  // Filter state for Equipment List
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedListingTypes, setSelectedListingTypes] = useState<string[]>([]);

  // Filter state for Equipment Requirements
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<string | null>(null);

  const [banner, setBanner] = useState<MarketplaceBanner | null>(null);
  const [filterOptionsData, setFilterOptionsData] = useState<MarketplaceFilterOptionsData | null>(null);
  const [filtersLoading, setFiltersLoading] = useState(true);

  // Load dynamic filter options
  useEffect(() => {
    getMarketplaceFilterOptions()
      .then((res) => {
        if (res) {
          setFilterOptionsData(res);
        }
      })
      .catch(() => {})
      .finally(() => setFiltersLoading(false));
  }, []);

  const equipmentFilters = filterOptionsData?.equipment;
  const requirementsFilters = filterOptionsData?.equipment_requirements || filterOptionsData?.equipment;

  const categories = equipmentFilters?.category || [];
  const listingTypes = equipmentFilters?.listing_type || [];
  const cities = requirementsFilters?.city || requirementsFilters?.location || [];
  const budgets = requirementsFilters?.budget || [];

  // Load banner on mount
  useEffect(() => {
    getMarketplaceBanners("marketplace_home")
      .then((res) => {
        if (res && res.success && res.data && res.data.results && res.data.results.length > 0) {
          setBanner(res.data.results[0]);
        }
      })
      .catch(() => {});
  }, []);

  // Load current user profile and user account ID
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const cachedUser = localStorage.getItem("user_info");
        if (cachedUser) {
          const parsedUser = JSON.parse(cachedUser);
          if (parsedUser.id) setMyUserId(parsedUser.id);
        }
        const res = await getMarketplaceProfile();
        if (res?.data?.id) {
          setMyProfileId(res.data.id);
        } else {
          const cached = localStorage.getItem("marketplace_profile");
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.id) setMyProfileId(parsed.id);
          }
        }
      } catch (e) {
        const cached = localStorage.getItem("marketplace_profile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed.id) setMyProfileId(parsed.id);
          } catch {}
        }
      }
    };
    if (localStorage.getItem("access_token")) {
      loadProfile();
    }
    window.addEventListener("marketplace-auth-change", loadProfile);
    return () => {
      window.removeEventListener("marketplace-auth-change", loadProfile);
    };
  }, []);

  // Update local search state if URL search query updates
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSearch = params.get("search") || "";
    setSearch(urlSearch);
  }, [window.location.search]);
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "list") {
        const categoryFilter = selectedCategories.length ? selectedCategories.join(",") : undefined;
        const listingTypeFilter = selectedListingTypes.length === 1 ? selectedListingTypes[0] as any : undefined;

        const res = await getPublicListings({ 
          page, 
          page_size: 9, 
          search: search || undefined,
          category: categoryFilter,
          listing_type: listingTypeFilter
        });
        setListings(res.results ?? []);
        setRequirements([]);
        setTotalCount(res.count ?? 0);
      } else {
        const cityFilter = selectedCities.length ? selectedCities.join(",") : undefined;

        const res = await getPublicRequirements({
          page,
          page_size: 9,
          search: search || undefined,
          requirement_type: "equipment_requirement",
          location: cityFilter,
          city: cityFilter,
          budget: selectedBudget || undefined,
        });

        let results = res.results ?? [];

        // Client-side tier fallback if backend returns unparsed budget
        if (selectedBudget) {
          results = results.filter(req => {
            if (!req.budget) return selectedBudget === "flexible";
            const num = parseFloat(req.budget.replace(/[^0-9.]/g, ""));
            if (isNaN(num)) return true;
            if (selectedBudget === "under_5k") return num < 5000;
            if (selectedBudget === "5k_15k") return num >= 5000 && num <= 15000;
            if (selectedBudget === "15k_50k") return num >= 15000 && num <= 50000;
            if (selectedBudget === "50k_plus") return num > 50000;
            if (selectedBudget === "flexible") return true;
            return true;
          });
        }

        setRequirements(results);
        setListings([]);
        setTotalCount(res.count ?? results.length);
      }
    } catch {
      setListings([]);
      setRequirements([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, selectedCategories, selectedListingTypes, selectedCities, selectedBudget]);

  const handleCategoryChange = (cat: string) => {
    setSelectedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
    setPage(1);
  };

  const handleListingTypeChange = (type: string) => {
    setSelectedListingTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
    setPage(1);
  };

  const handleCityChange = (city: string) => {
    setSelectedCities(prev => 
      prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
    );
    setPage(1);
  };

  const handleBudgetChange = (budgetVal: string) => {
    setSelectedBudget(prev => prev === budgetVal ? null : budgetVal);
    setPage(1);
  };

  const handleToggleSaveListing = async (e: React.MouseEvent, listingId: string) => {
    e.stopPropagation();
    if (!localStorage.getItem("access_token")) {
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    const wasSaved = !!savedListings[listingId];
    // Optimistic update
    setSavedListings(prev => ({ ...prev, [listingId]: !wasSaved }));
    try {
      if (wasSaved) {
        await unsaveListing(listingId);
        showSuccessToast("Listing removed from saved items.");
      } else {
        await saveListing(listingId);
        showSuccessToast("Listing saved to your wishlist!");
      }
    } catch {
      // Revert on error
      setSavedListings(prev => ({ ...prev, [listingId]: wasSaved }));
      showErrorToast("Could not update saved items. Please try again.");
    }
  };

  const handleToggleSaveRequirement = async (e: React.MouseEvent, reqId: string) => {
    e.stopPropagation();
    if (!localStorage.getItem("access_token")) {
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    const wasSaved = !!savedRequirements[reqId];
    setSavedRequirements(prev => ({ ...prev, [reqId]: !wasSaved }));
    try {
      if (wasSaved) {
        await unsaveRequirement(reqId);
        showSuccessToast("Job requirement removed from saved items.");
      } else {
        await saveRequirement(reqId);
        showSuccessToast("Job requirement saved!");
      }
    } catch {
      setSavedRequirements(prev => ({ ...prev, [reqId]: wasSaved }));
      showErrorToast("Could not update saved items. Please try again.");
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync is_saved from API response into local saved state
  useEffect(() => {
    if (listings.length > 0 && localStorage.getItem("access_token")) {
      const map: Record<string, boolean> = {};
      listings.forEach(l => { if (l.is_saved) map[l.id] = true; });
      setSavedListings(prev => ({ ...prev, ...map }));
    }
  }, [listings]);

  useEffect(() => {
    if (requirements.length > 0 && localStorage.getItem("access_token")) {
      const map: Record<string, boolean> = {};
      requirements.forEach(r => { if (r.is_saved) map[r.id] = true; });
      setSavedRequirements(prev => ({ ...prev, ...map }));
    }
  }, [requirements]);

  const handleRevealContact = async (reqId: string) => {
    const hasToken = !!localStorage.getItem("access_token");
    if (!hasToken) {
      showErrorToast("Please log in first to view contact details.");
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    if (!checkProfileCompleteness()) {
      showErrorToast("Please complete your profile details first to view contact details.");
      navigate("/marketplace/profile/create");
      return;
    }
    try {
      const res = await apiCall({
        method: "POST",
        url: `/api/v1/marketplace/requirements/${reqId}/reveal-contact/`
      });
      if (res) {
        setRevealedContacts(prev => ({
          ...prev,
          [reqId]: {
            email: res.contact_email || "N/A",
            phone: res.contact_phone || "N/A"
          }
        }));
        showSuccessToast("Contact details revealed!");
      }
    } catch {
      showErrorToast("Failed to reveal contact details. Please try again.");
    }
  };

  const handleApplyClick = (req: MarketplaceRequirement) => {
    const hasToken = !!localStorage.getItem("access_token");
    if (!hasToken) {
      showErrorToast("Please log in first to apply.");
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    if (isAccountFrozen()) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot apply to requirements.");
      return;
    }
    if (!checkProfileCompleteness()) {
      showErrorToast("Please complete your profile details first to apply.");
      navigate("/marketplace/profile/create");
      return;
    }
    setSelectedReq(req);
    setApplyMessage("");
    setApplyRate("");
    setIsApplyModalOpen(true);
  };

  const handleSubmitApplication = async () => {
    if (!selectedReq) return;
    setSubmittingApply(true);
    try {
      await applyToRequirement(selectedReq.id, {
        message: applyMessage || undefined,
        proposed_rate: applyRate || undefined
      });
      setAppliedJobs(prev => ({ ...prev, [selectedReq.id]: true }));
      showSuccessToast("Successfully applied for this job requirement!");
      setIsApplyModalOpen(false);
    } catch (err: any) {
      let errMsg = "Failed to submit application. Make sure your profile is set up.";
      if (err?.response?.data) {
        if (typeof err.response.data === "object") {
          errMsg = err.response.data.detail || err.response.data.message || err.response.data.error || Object.values(err.response.data).flat().join(" ") || errMsg;
        } else if (typeof err.response.data === "string") {
          errMsg = err.response.data;
        }
      }
      showErrorToast(errMsg);
    } finally {
      setSubmittingApply(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setPage(1);
      fetchData();
    }
  };

  const renderPagination = () => {
    const currentCount = activeTab === "list" ? listings.length : requirements.length;
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: "auto", paddingTop: 48 }}>
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--mp-headingColor)", opacity: page === 1 ? 0.3 : 1 }}>
          <ChevronLeft size={20} />
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ width: 32, height: 32, borderRadius: 4, background: "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))", color: "#fff", border: "none", fontWeight: 600 }}>{page}</button>
          <span style={{ display: "flex", alignItems: "flex-end", padding: "0 4px", color: "var(--mp-featureDescColor)" }}>...</span>
        </div>
        <button disabled={currentCount < 9} onClick={() => setPage(p => p + 1)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--mp-headingColor)", opacity: currentCount < 9 ? 0.3 : 1 }}>
          <ChevronRight size={20} />
        </button>
      </div>
    );
  };

  return (
    <PageWrapper>
      {loading && listings.length === 0 && requirements.length === 0 && (
        <MarketplaceLoader fullScreen />
      )}
      {/* ── SIDEBAR ── */}
      <Sidebar>
        <SidebarTitle>Filters</SidebarTitle>
        
        {activeTab === "list" ? (
          <>
            <FilterGroup>
              <FilterGroupTitle>Category</FilterGroupTitle>
              <FilterList>
                {filtersLoading ? (
                  <span style={{ fontSize: "12px", color: "var(--mp-featureDescColor, #71717a)" }}>Loading categories...</span>
                ) : categories.length > 0 ? (
                  categories.map((cat) => {
                    const val = cat.value || cat.slug || "";
                    return (
                      <FilterLabel key={val}>
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(val)}
                          onChange={() => handleCategoryChange(val)}
                        />
                        {cat.label}
                      </FilterLabel>
                    );
                  })
                ) : (
                  <span style={{ fontSize: "12px", color: "var(--mp-featureDescColor, #71717a)" }}>No categories available</span>
                )}
              </FilterList>
            </FilterGroup>

            <FilterGroup>
              <FilterGroupTitle>Listing Type</FilterGroupTitle>
              <FilterList>
                {filtersLoading ? (
                  <span style={{ fontSize: "12px", color: "var(--mp-featureDescColor, #71717a)" }}>Loading types...</span>
                ) : listingTypes.length > 0 ? (
                  listingTypes.map((t) => (
                    <FilterLabel key={t.value}>
                      <input
                        type="checkbox"
                        checked={selectedListingTypes.includes(t.value)}
                        onChange={() => handleListingTypeChange(t.value)}
                      />
                      {t.label}
                    </FilterLabel>
                  ))
                ) : (
                  <span style={{ fontSize: "12px", color: "var(--mp-featureDescColor, #71717a)" }}>No types available</span>
                )}
              </FilterList>
            </FilterGroup>
          </>
        ) : (
          <>
            <FilterGroup>
              <FilterGroupTitle>City</FilterGroupTitle>
              <FilterList>
                {filtersLoading ? (
                  <span style={{ fontSize: "12px", color: "var(--mp-featureDescColor, #71717a)" }}>Loading cities...</span>
                ) : cities.length > 0 ? (
                  cities.map((c) => (
                    <FilterLabel key={c.value}>
                      <input
                        type="checkbox"
                        checked={selectedCities.includes(c.value)}
                        onChange={() => handleCityChange(c.value)}
                      />
                      {c.label}
                    </FilterLabel>
                  ))
                ) : (
                  <span style={{ fontSize: "12px", color: "var(--mp-featureDescColor, #71717a)" }}>No cities available</span>
                )}
              </FilterList>
            </FilterGroup>

            <FilterGroup>
              <FilterGroupTitle>Budget</FilterGroupTitle>
              <FilterList>
                {filtersLoading ? (
                  <span style={{ fontSize: "12px", color: "var(--mp-featureDescColor, #71717a)" }}>Loading budgets...</span>
                ) : budgets.length > 0 ? (
                  budgets.map((b) => (
                    <FilterLabel key={b.value}>
                      <input
                        type="radio"
                        name="requirement_budget"
                        checked={selectedBudget === b.value}
                        onChange={() => handleBudgetChange(b.value)}
                        onClick={() => {
                          if (selectedBudget === b.value) {
                            setSelectedBudget(null);
                            setPage(1);
                          }
                        }}
                      />
                      {b.label}
                    </FilterLabel>
                  ))
                ) : (
                  <span style={{ fontSize: "12px", color: "var(--mp-featureDescColor, #71717a)" }}>No budget tiers available</span>
                )}
              </FilterList>
            </FilterGroup>
          </>
        )}
      </Sidebar>

      {/* ── MAIN CONTENT ── */}
      <MainContent>
        <TopBar>
          <TopBarLeft style={{ gap: "24px" }}>
            <PageTitle>Equipment</PageTitle>
            <ToggleGroup>
              <ToggleBtn $active={activeTab === "list"} onClick={() => { setActiveTab("list"); setPage(1); }}>Equipment List</ToggleBtn>
              <ToggleBtn $active={activeTab === "requirement"} onClick={() => { setActiveTab("requirement"); setPage(1); }}>Equipment Requirements</ToggleBtn>
            </ToggleGroup>
            <ResultBadge>{totalCount.toLocaleString()} Results</ResultBadge>
          </TopBarLeft>
          
          <TopBarRight>
            <SearchInputWrap>
              <Search />
              <SearchInput 
                placeholder="Search models, brands, or specs..." 
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleSearchSubmit}
              />
            </SearchInputWrap>
            <ToggleGroup>
              <ToggleBtn $active={viewMode === "grid"} onClick={() => setViewMode("grid")}><Grid size={14} /></ToggleBtn>
              <ToggleBtn $active={viewMode === "list"} onClick={() => setViewMode("list")}><ListIcon size={14} /></ToggleBtn>
            </ToggleGroup>
          </TopBarRight>
        </TopBar>

        {activeTab === "list" ? (
          <EquipmentGrid $viewMode={viewMode}>
            {listings.map((listing, i) => {
                  const img = listing.cover_image || listing.image_url || listing.images?.[0]?.image_url;
                  const isMine = myProfileId != null && listing.profile_id === myProfileId;
                  
                  const card = (
                    <EquipCard 
                      key={listing.id} 
                      $viewMode={viewMode}
                      onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                    >
                      <EquipCardImageWrap $viewMode={viewMode}>
                        {img && <EquipCardImage src={resolveImage(img)} alt={listing.title} />}
                        <FavBtn
                          $saved={!!savedListings[listing.id]}
                          onClick={(e) => handleToggleSaveListing(e, listing.id)}
                          title={savedListings[listing.id] ? "Remove from saved" : "Save listing"}
                        >
                          <Bookmark size={16} />
                        </FavBtn>
                      </EquipCardImageWrap>
                      
                      <EquipCardBody>
                        <TitleRow>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <EquipTitle>{listing.title}</EquipTitle>
                            {isMine && (
                              <span style={{ alignSelf: "flex-start", fontSize: "9px", padding: "1px 6px", background: "var(--mp-heroBadgeBg, rgba(255, 213, 115, 0.2))", color: "var(--mp-featureIconColor, #D863BA)", borderRadius: "100px", fontWeight: 700 }}>
                                MY LISTING
                              </span>
                            )}
                          </div>
                          {(listing.avg_rating !== undefined && listing.review_count !== undefined && listing.review_count > 0) && (
                            <RatingScore><Star /> {Number(listing.avg_rating).toFixed(1)}</RatingScore>
                          )}
                        </TitleRow>
                        
                        <EquipDesc>
                          {listing.category_name || "Professional production equipment"} in excellent condition. 
                          {listing.location ? ` Located in ${listing.location}.` : ""}
                        </EquipDesc>

                        <PriceRow>
                          {listing.listing_type === "rent" ? (
                            <PriceBlock>
                              <PriceLabel>RENT / DAY</PriceLabel>
                              <PriceValue>₹{listing.price}</PriceValue>
                            </PriceBlock>
                          ) : null}
                          
                          {listing.listing_type === "sale" ? (
                            <PriceBlock>
                              <PriceLabel>BUY NOW</PriceLabel>
                              <PriceValue>₹{listing.price}</PriceValue>
                            </PriceBlock>
                          ) : null}
                          
                          {listing.listing_type !== "rent" && listing.listing_type !== "sale" && (
                            <PriceBlock>
                              <PriceLabel>PRICE</PriceLabel>
                              <PriceValue>₹{listing.price}</PriceValue>
                            </PriceBlock>
                          )}
                        </PriceRow>

                        <ActionRow>
                          <ActionBtnPrimary 
                            disabled={isMine} 
                            style={isMine ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              if (!isMine) navigate(`/marketplace/listing/${listing.id}`); 
                            }}
                          >
                            {listing.listing_type === "rent" ? "Rent Now" : "Buy Now"}
                          </ActionBtnPrimary>
                          <ActionBtnSecondary onClick={(e) => { e.stopPropagation(); navigate(`/marketplace/listing/${listing.id}`); }}>
                            Details
                          </ActionBtnSecondary>
                        </ActionRow>
                      </EquipCardBody>
                    </EquipCard>
                  );

                  if (i === 3 && banner) {
                    const bgImage = (banner.image_url ? resolveImage(banner.image_url) : "") || equipmentBannerBg;
                    return (
                      <React.Fragment key="banner-frag">
                        {card}
                        <BannerBox $viewMode={viewMode} $bgUrl={bgImage}>
                          <BannerOverlay />
                          <BannerContent>
                            {banner.tagline && <BannerEyebrow>{banner.tagline}</BannerEyebrow>}
                            <BannerTitle>{banner.headline}</BannerTitle>
                            {banner.description && <BannerDesc>{banner.description}</BannerDesc>}
                            {banner.is_cta_enabled && banner.cta_label && (
                              <BannerBtn 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (banner.cta_url) {
                                    if (banner.cta_url.startsWith("http://") || banner.cta_url.startsWith("https://")) {
                                      window.open(banner.cta_url, "_blank");
                                    } else {
                                      navigate(banner.cta_url);
                                    }
                                  }
                                }}
                              >
                                {banner.cta_label}
                              </BannerBtn>
                            )}
                          </BannerContent>
                          <BannerFooter>EST. 2026 - PREMIUM ONLY</BannerFooter>
                        </BannerBox>
                      </React.Fragment>
                    );
                  }

                  return card;
                })}
          </EquipmentGrid>
        ) : (
          <EquipmentGrid $viewMode={viewMode}>
            {requirements.map((req) => {
                  const revealed = revealedContacts[req.id] || (req.is_contact_revealed ? { email: req.contact_email || "N/A", phone: req.contact_phone || "N/A" } : null);
                  const applied = appliedJobs[req.id];
                  const cachedUser = localStorage.getItem("user_info") ? JSON.parse(localStorage.getItem("user_info")!) : null;
                  const cachedProfile = localStorage.getItem("marketplace_profile") ? JSON.parse(localStorage.getItem("marketplace_profile")!) : null;
                  const myName = cachedProfile?.display_name || cachedUser?.name || cachedUser?.username || "";
                  const isMine = (myProfileId != null && req.profile_id === myProfileId) || 
                                 (myName && req.poster_name && myName.toLowerCase() === req.poster_name.toLowerCase());
                  return (
                    <EquipCard key={req.id} $viewMode={viewMode}>
                      <EquipCardBody style={{ gap: "12px", position: "relative" }}>
                        <FavBtn
                          $saved={!!savedRequirements[req.id]}
                          onClick={(e) => handleToggleSaveRequirement(e, req.id)}
                          title={savedRequirements[req.id] ? "Remove from saved" : "Save job"}
                          style={{ position: "absolute", top: 12, right: 12 }}
                        >
                          <Bookmark size={16} />
                        </FavBtn>
                        <TitleRow>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <EquipTitle>{req.title}</EquipTitle>
                            {isMine && (
                              <span style={{ alignSelf: "flex-start", fontSize: "9px", padding: "1px 6px", background: "var(--mp-heroBadgeBg, rgba(255, 213, 115, 0.2))", color: "var(--mp-featureIconColor, #D863BA)", borderRadius: "100px", fontWeight: 700 }}>
                                MY REQUIREMENT
                              </span>
                            )}
                          </div>
                        </TitleRow>
                        
                        <EquipDesc>{req.description || "Looking for reliable production assistance/equipment."}</EquipDesc>

                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", background: "var(--mp-bodyBackground, rgba(0,0,0,0.03))", padding: "10px", borderRadius: "8px" }}>
                          <div style={{ color: "var(--mp-textColor)" }}><strong>Location:</strong> {req.location || "Anywhere"}</div>
                          <div style={{ color: "var(--mp-textColor)" }}><strong>Budget:</strong> {req.budget ? `₹${req.budget}` : "Negotiable"}</div>
                          
                          {revealed ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: "6px", marginTop: "4px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "green", fontWeight: 600 }}>
                                <Phone size={12} /> {revealed.phone}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "green", fontWeight: 600 }}>
                                <Mail size={12} /> {revealed.email}
                              </div>
                            </div>
                          ) : (
                            !isMine ? (
                              <button 
                                onClick={() => handleRevealContact(req.id)}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", background: "none", border: "1px dashed #c94daa", color: "#c94daa", fontSize: "11px", fontWeight: 700, padding: "6px", borderRadius: "6px", cursor: "pointer", marginTop: "4px" }}
                              >
                                <Phone size={10} /> View Contact Details
                              </button>
                            ) : null
                          )}
                        </div>

                        <ActionRow style={{ marginTop: "auto" }}>
                          {applied ? (
                            <button disabled style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: "#e2e8f0", color: "#64748b", fontWeight: 700, fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                              <Check size={14} /> Applied
                            </button>
                          ) : (
                            <ActionBtnPrimary 
                              disabled={isMine} 
                              style={isMine ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                              onClick={() => {
                                if (!isMine) handleApplyClick(req);
                              }}
                            >
                              Apply Now
                            </ActionBtnPrimary>
                          )}
                        </ActionRow>
                      </EquipCardBody>
                    </EquipCard>
                  );
                })}
          </EquipmentGrid>
        )}

        {renderPagination()}
      </MainContent>

      {isApplyModalOpen && selectedReq && (
        <ModalOverlay onClick={() => setIsApplyModalOpen(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Submit Application</ModalTitle>
              <CloseBtn onClick={() => setIsApplyModalOpen(false)}>&times;</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <ModalField>
                <label style={{ fontWeight: 600, color: "var(--mp-textColor, #171717)" }}>Applying for:</label>
                <span style={{ fontSize: "14px", color: "var(--mp-headingColor, #171717)", fontWeight: 700 }}>{selectedReq.title}</span>
              </ModalField>
              <ModalField>
                <label htmlFor="coverNote">Cover Note / Pitch (optional)</label>
                <textarea
                  id="coverNote"
                  value={applyMessage}
                  onChange={(e) => setApplyMessage(e.target.value)}
                  placeholder="Introduce yourself, mention your experience or details of the equipment/service you can offer..."
                  rows={4}
                />
              </ModalField>
              <ModalField>
                <label htmlFor="proposedRate">Proposed Rate (₹, optional)</label>
                <input
                  id="proposedRate"
                  type="number"
                  value={applyRate}
                  onChange={(e) => setApplyRate(e.target.value)}
                  placeholder="e.g. 15000"
                />
              </ModalField>
            </ModalBody>
            <ModalFooter>
              <CancelBtn onClick={() => setIsApplyModalOpen(false)}>Cancel</CancelBtn>
              <SubmitBtn disabled={submittingApply} onClick={handleSubmitApplication}>
                {submittingApply ? "Submitting..." : "Submit Application"}
              </SubmitBtn>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: var(--mp-cardBackground, #ffffff);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  gap: 20px;
  animation: modalFadeIn 0.3s ease-out;

  @keyframes modalFadeIn {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 12px;
  
  [data-marketplace-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
  font-family: Montserrat, sans-serif;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--mp-textColor, #71717a);
  &:hover {
    color: var(--mp-headingColor, #171717);
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: var(--mp-textColor, #4b5563);
  }

  textarea, input {
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: var(--mp-inputBackground, #ffffff);
    color: var(--mp-textColor, #171717);
    font-size: 14px;
    outline: none;
    font-family: inherit;

    [data-marketplace-theme="dark"] & {
      border-color: rgba(255, 255, 255, 0.15);
      background: rgba(255, 255, 255, 0.03);
    }

    &:focus {
      border-color: #c94daa;
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 16px;

  [data-marketplace-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const CancelBtn = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: none;
  color: var(--mp-textColor, #4b5563);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  
  [data-marketplace-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.15);
  }

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const SubmitBtn = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default MarketplaceEquipmentPage;
